/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Video Reviews Section JS
 * -------------------------------------------------
 * Fully scoped per section instance — safe to add multiple
 * times on the same page and safe inside custom.js.
 *
 * Usage: paste this entire file into your theme's custom.js
 *        (Assets → custom.js) or enqueue it separately.
 * -------------------------------------------------
 */

(function () {
  'use strict';

  function initVideoReviews(section) {
    // Prevent double-init
    if (section.dataset.vrInit === 'true') return;
    section.dataset.vrInit = 'true';

    var scroll = section.querySelector('.video-reviews__scroll');
    var thumb = section.querySelector('.video-reviews__scrollbar-thumb');
    var modal = section.querySelector('.video-reviews__modal');
    var iframe = modal.querySelector('iframe');
    var backdrop = modal.querySelector('.video-reviews__modal-backdrop');
    var closeBtn = modal.querySelector('.video-reviews__modal-close');
    var arrowLeft = section.querySelector('.video-reviews__arrow--left');
    var arrowRight = section.querySelector('.video-reviews__arrow--right');

    // Scroll arrows
    if (arrowLeft) {
      arrowLeft.addEventListener('click', function () {
        scroll.scrollBy({ left: -320, behavior: 'smooth' });
      });
    }
    if (arrowRight) {
      arrowRight.addEventListener('click', function () {
        scroll.scrollBy({ left: 320, behavior: 'smooth' });
      });
    }

    // Custom scrollbar thumb
    function updateThumb() {
      if (!scroll || !thumb) return;
      var maxScroll = scroll.scrollWidth - scroll.clientWidth;
      if (maxScroll <= 0) { thumb.style.width = '100%'; thumb.style.left = '0'; return; }
      var ratio = scroll.scrollLeft / maxScroll;
      var thumbWidth = Math.max(20, (scroll.clientWidth / scroll.scrollWidth) * 100);
      thumb.style.width = thumbWidth + '%';
      thumb.style.left = ratio * (100 - thumbWidth) + '%';
    }
    if (scroll) {
      scroll.addEventListener('scroll', updateThumb);
      window.addEventListener('resize', updateThumb);
      updateThumb();
    }

    // Open modal
    var thumbnails = section.querySelectorAll('.video-reviews__thumbnail');
    thumbnails.forEach(function (card) {
      card.addEventListener('click', function () {
        var videoId = this.dataset.videoId;
        var name = this.dataset.reviewer;
        var location = this.dataset.location;
        var text = this.dataset.reviewText;
        var tag = this.dataset.tag;
        var product = this.dataset.product;

        var elName = modal.querySelector('[data-role="modalName"]');
        var elLocation = modal.querySelector('[data-role="modalLocation"]');
        var elText = modal.querySelector('[data-role="modalText"]');
        var elTag = modal.querySelector('[data-role="modalTag"]');
        var elAvatar = modal.querySelector('[data-role="modalAvatar"]');
        var elmodalLikes = modal.querySelector('[data-role="modalLikes"]');

        if (elName) elName.textContent = name;
        if (elLocation) elLocation.textContent = location + ' · ' + product;
        if (elText) elText.textContent = '"' + text + '"';
        if (elTag) elTag.textContent = tag;
        if (elAvatar) elAvatar.textContent = name.split(' ').map(function (n) { return n[0]; }).join('');
        if (elmodalLikes) elmodalLikes.textContent = '1.2k logon ko helpful laga';

        iframe.src = 'https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0';
        modal.classList.add('video-reviews__modal--open');
        document.body.style.overflow = 'hidden';
      });
    });

    // Close modal
    function closeModal() {
      modal.classList.remove('video-reviews__modal--open');
      iframe.src = '';
      document.body.style.overflow = '';
    }
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (backdrop) backdrop.addEventListener('click', closeModal);

    // Escape key — only close this section's modal
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('video-reviews__modal--open')) {
        closeModal();
      }
    });
  }

  // Init all instances on page load
  function initAll() {
    document.querySelectorAll('[data-section-type="video-reviews"]').forEach(initVideoReviews);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Support Shopify Theme Editor (live reload in customizer)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var section = e.target.querySelector('[data-section-type="video-reviews"]');
      if (section) initVideoReviews(section);
    });
  }
})();

$(document).ready(function(){
  if(window.location.hash.indexOf('#opencartdrawer') > -1) {
    document.getElementById('mini-cart').open = true;
    const url = window.location.href.replace("#opencartdrawer", "");
    history.replaceState(null, null, url);
  }
  
  $('body').on('click','.tab_button', function(){
    $(this).closest('.tab_content_wrapper').find('.tab_content').slideToggle();
    $(this).closest('.tab_content_wrapper').find('.tab_content').toggleClass('active');
  });

  $('body').on('click','.custom_filter_wp .checkbox-container input[type="checkbox"],.custom_outer_custom_filter input[type="checkbox"]',function(e){
    e.preventDefault();
    var _this = $(this);
    var href = $(this).attr('name');
    var val = $(this).attr('value');
    if(val != undefined && val != ''){
      if(val.indexOf(' ') >= 0){
        val = val.replaceAll(' ','+').replaceAll('&','%26');
      }
      if(val.indexOf('&') >= 0){
        val = val.replaceAll('&','%26');
      }
      var currentURL = window.location.href;
      var finalVAL = href+'='+val;
      var is_current = false;
      var without_cur = [];
      var params = {};
      var parser = document.createElement('a');
      parser.href = window.location.href;
      var query = parser.search.substring(1);
      var vars = query.split('&');
      for (var i = 0; i < vars.length; i++) {
        var pair = vars[i].split('=');        
        if(encodeURI(href) == pair[0] && val == pair[1]){
          is_current = true;
        }else{
          if(_this.closest('.custom_filter_wp').hasClass('single_value')){
            if(encodeURI(href) == pair[0]){
             without_cur.push(pair[0] + '=' + val);    
            }else{
             without_cur.push(pair[0] + '=' + pair[1]);    
            }
          }else{
           without_cur.push(pair[0] + '=' + pair[1]); 
          }          
        }
      }
      if(is_current){               
        currentURL = (currentURL.indexOf('?') > -1) ? currentURL.split('?')[0] : currentURL;
        if(without_cur.length > 0){
          currentURL = currentURL + '?' +without_cur.join('&')
        }else{              
          currentURL = currentURL;
        }            
      }else{   
        if(_this.closest('.custom_filter_wp').hasClass('single_value') && currentURL.indexOf(href) > -1){
          var newurl = [];
          currentURL = (currentURL.indexOf('?') > -1) ? currentURL.split('?')[0] : currentURL;
          for (var i = 0; i < vars.length; i++) {
            var pair = vars[i].split('=');        
            if(encodeURI(href) == pair[0]){
              newurl.push(finalVAL);
            }else{                
              newurl.push(pair[0] + '=' + pair[1]);                 
            }
          }
          if(currentURL.indexOf('?') >= 0){
            currentURL = currentURL + '&' + newurl.join('&');
          }else{
            currentURL = currentURL + '?' + newurl.join('&');
          }
        }else{
         if(currentURL.indexOf('?') >= 0){
            currentURL = currentURL + '&' + finalVAL;
          }else{
            currentURL = currentURL + '?' + finalVAL;
          }  
        }        
      }         
      this.dispatchEvent(new CustomEvent("facet:criteria-changed", {
        bubbles: true,
        detail: {
          url: currentURL.toString()
        }
      }));
    }
  });
});